import { type PoRun, poRefreshData } from '@repo/sdk/gen'
import { StatusButton } from '@repo/ui/aceternity/stateful-button'
import { Card, CardContent } from '@repo/ui/card'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { formatLocaleDate } from '@repo/ui/dates'
import { Progress } from '@repo/ui/progress'
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
	AlertTriangle,
	ArrowRight,
	CheckCircle2,
	FilePlus2
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
	datasetUpToDate,
	failedTables,
	isRefreshInFlight,
	staleDays,
	updateInProgress
} from '@/lib/data-freshness'
import { pageTitle, type TKey, translateApp, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import {
	AgentCard,
	DataCard
} from '@/routes/(private)/-components/system-cards'
import { DatasetGaps } from '@/routes/(private)/documents/-components/dataset-notices'
import { RunStatusBadge } from '@/routes/(private)/documents/-components/run-status-badge'
import { poErrorMessage } from '@/routes/(private)/documents/-lib/error-message'
import {
	runsListQueryOptions,
	systemStatusQueryOptions
} from '@/routes/(private)/documents/-lib/query-options'

// How long «Dati aggiornati» holds its check once the agent closes the round.
const REFRESH_DONE_MS = 2500

export const Route = createFileRoute('/(private)/')({
	loader: ({ context }) =>
		Promise.all([
			context.queryClient.ensureQueryData(systemStatusQueryOptions()),
			context.queryClient.ensureQueryData(
				runsListQueryOptions({ page: 1, limit: 1 })
			)
		]),
	component: Dashboard,
	head: () => ({
		meta: [
			{ title: pageTitle(translateApp('it', 'admin:home.page_title')) }
		]
	})
})

function Dashboard() {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	// While a refresh is on its way the dashboard polls faster, so the button
	// moves from «in attesa» to «in corso» to done close to when it happens.
	const status = useSuspenseQuery({
		...systemStatusQueryOptions(),
		refetchInterval: (query) =>
			isRefreshInFlight(query.state.data?.refresh?.status)
				? 10_000
				: 30_000
	})
	const runs = useSuspenseQuery(runsListQueryOptions({ page: 1, limit: 1 }))
	const lastRun = runs.data.data[0]

	const refreshData = useMutation({
		mutationFn: () => poRefreshData({}),
		onSuccess: async () => {
			// Resolves once the status shows the queued command, so the spinner
			// hands over from the click to the server without a gap.
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.system()
			})
		},
		onError: (error) => toast.error(poErrorMessage(error))
	})

	const data = status.data
	const dataset = data.dataset
	const refresh = data.refresh
	// Stale data is announced by the app shell's banner, on every page; the
	// dashboard only confirms when the next document reads every table at the
	// dataset's date and that date is recent. The gaps, the tables left behind
	// and the failures say what is wrong otherwise.
	const asOf = dataset.asOf
	const fresh =
		asOf !== null &&
		datasetUpToDate(data) &&
		staleDays(asOf) === null &&
		data.tables.every((table) => staleDays(table.confirmedAt) === null)
	const failed = failedTables(data)

	// The refresh is followed on the server's record of the agent command, so it
	// survives a reload and ends when the agent closes its round, not when some
	// table happens to change (an unchanged table is never re-sent).
	const refreshInFlight =
		refreshData.isPending || isRefreshInFlight(refresh?.status)
	// «Dati aggiornati» only when the round brought every table: a completed
	// round that left one behind, or failed on one, lets the warnings speak.
	const [followingRefresh, setFollowingRefresh] = useState(refreshInFlight)
	const [refreshDone, setRefreshDone] = useState(false)
	if (refreshInFlight !== followingRefresh) {
		setFollowingRefresh(refreshInFlight)
		setRefreshDone(
			!refreshInFlight &&
				refresh?.status === 'completed' &&
				datasetUpToDate(data)
		)
	}
	useEffect(() => {
		if (!refreshDone) {
			return
		}
		const timeout = setTimeout(() => setRefreshDone(false), REFRESH_DONE_MS)
		return () => clearTimeout(timeout)
	}, [refreshDone])

	return (
		<div className='mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-6'>
			<div className='flex animate-enter flex-wrap items-center justify-between gap-3'>
				<h1 className='font-semibold text-2xl tracking-tight'>
					{t('admin:home.page_title')}
				</h1>
				{/* A live region always mounted: the outcome of the refresh
				    arrives with the polling, after the button stops, and a
				    screen reader announces it with the button's new label. */}
				<div
					aria-live='polite'
					className='flex flex-wrap items-center gap-3'
				>
					{refresh?.status === 'stalled' && !refreshInFlight ? (
						<RefreshWarning>
							{t('po:dashboard.refresh_stalled')}
						</RefreshWarning>
					) : null}
					{/* The round closed without a table every document needs: the
					    documents still read the data of before. */}
					{refresh?.status === 'incomplete' && !refreshInFlight ? (
						<RefreshWarning>
							{failed.length > 0
								? t('po:dashboard.refresh_incomplete_tables', {
										list: failed.join(', ')
									})
								: t('po:dashboard.refresh_incomplete')}
						</RefreshWarning>
					) : null}
					<StatusButton
						onClick={() => refreshData.mutate()}
						phase={refreshPhase(refreshInFlight, refreshDone)}
						variant='outline'
					>
						{t(refreshLabel(refreshDone, refreshInFlight))}
					</StatusButton>
				</div>
			</div>

			{fresh && (
				<FreshDataBanner
					message={t('po:dashboard.trust_fresh', {
						date: formatLocaleDate(asOf, 'P HH:mm')
					})}
				/>
			)}
			<DatasetGaps dataset={dataset} />

			<HeroRun run={lastRun} />

			<div className='grid animate-enter items-start gap-4 [--enter-step:3] md:grid-cols-2'>
				<DataCard
					dataset={dataset}
					tables={data.tables}
					updating={updateInProgress(data)}
				/>
				<AgentCard agent={data.agent} />
			</div>
		</div>
	)
}

function RefreshWarning(props: { children: ReactNode }) {
	return (
		<span className='flex items-center gap-1.5 text-amber-700 text-sm dark:text-amber-400'>
			<AlertTriangle className='size-4 shrink-0' />
			{props.children}
		</span>
	)
}

function FreshDataBanner(props: { message: string }) {
	return (
		<div className='flex animate-enter items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-50 px-4 py-2.5 text-emerald-900 text-sm [--enter-step:1] dark:bg-emerald-950 dark:text-emerald-200'>
			<CheckCircle2 className='size-4 shrink-0' />
			<span>{props.message}</span>
		</div>
	)
}

// The centrepiece: the current campaign document with its review progress and
// the next action. Handles empty (no run) and in-progress states.
function HeroRun(props: { run: PoRun | undefined }) {
	const { t } = useTranslation()
	const run = props.run

	if (!run) {
		return (
			<Card className='animate-enter py-0 [--enter-step:2]'>
				<CardContent className='flex flex-col items-center gap-2 py-12 text-center'>
					<FilePlus2 className='size-8 text-muted-foreground' />
					<p className='font-medium'>
						{t('po:dashboard.no_run_yet')}
					</p>
					<p className='max-w-md text-muted-foreground text-sm'>
						{t('po:dashboard.no_run_hint')}
					</p>
				</CardContent>
			</Card>
		)
	}

	// The engine has produced rows to look at: its version comes only with its
	// result. A document that failed stays failed here even once archived.
	const generated = run.engineVersion !== null
	const failed =
		!generated && run.status !== 'pending' && run.status !== 'running'
	const toReview = Math.max(0, run.totalRows - run.reviewedRows)
	const pct =
		run.totalRows > 0
			? Math.round((run.reviewedRows / run.totalRows) * 100)
			: 0

	return (
		<Card className='animate-enter py-0 [--enter-step:2]'>
			<CardContent className='flex flex-col gap-6 py-6'>
				<div className='flex flex-wrap items-start justify-between gap-3'>
					<div className='min-w-0'>
						<p className='text-muted-foreground text-xs uppercase tracking-wide'>
							{t('po:dashboard.current_campaign', {
								year: run.campaign
							})}
						</p>
						<h2 className='truncate font-semibold text-xl tracking-tight'>
							{run.name}
						</h2>
					</div>
					<RunStatusBadge status={run.status} />
				</div>

				{generated && (
					<>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
							<HeroStat
								label={t('po:workspace.universe')}
								value={run.totalRows}
							/>
							<HeroStat
								label={t('po:workspace.to_review')}
								tone={toReview > 0 ? 'warn' : undefined}
								value={toReview}
							/>
							<HeroStat
								label={t('po:workspace.flagged')}
								tone={run.flaggedRows > 0 ? 'warn' : undefined}
								value={run.flaggedRows}
							/>
							<HeroStat
								label={t('po:workspace.copies')}
								value={run.totalCopies}
							/>
						</div>

						<div className='flex flex-col gap-1'>
							<div className='flex items-center justify-between text-xs'>
								<span className='text-muted-foreground'>
									{t('po:workspace.progress')}
								</span>
								<span className='font-medium tabular-nums'>
									{pct}%
								</span>
							</div>
							<Progress
								className='h-1.5 [&_[data-slot=progress-indicator]]:bg-emerald-500 [&_[data-slot=progress-track]]:bg-foreground/8'
								value={pct}
							/>
						</div>

						<div>
							<ButtonLink
								params={{ runId: run.id }}
								to='/documents/$runId'
							>
								{t('po:dashboard.open_document')}
								<ArrowRight />
							</ButtonLink>
						</div>
					</>
				)}

				{failed && (
					<div className='flex flex-col items-start gap-3'>
						<p className='text-muted-foreground text-sm'>
							{t('po:dashboard.run_failed')}
						</p>
						<ButtonLink
							params={{ runId: run.id }}
							to='/documents/$runId'
							variant='outline'
						>
							{t('po:dashboard.open_document')}
							<ArrowRight />
						</ButtonLink>
					</div>
				)}

				{(run.status === 'running' || run.status === 'pending') && (
					<p className='text-muted-foreground text-sm'>
						{t('po:detail_page.generating')}
					</p>
				)}
			</CardContent>
		</Card>
	)
}

function HeroStat(props: { label: string; value: number; tone?: 'warn' }) {
	return (
		<div className='flex flex-col gap-0.5'>
			<span
				className={
					props.tone === 'warn'
						? 'font-semibold text-2xl text-amber-600 tabular-nums dark:text-amber-400'
						: 'font-semibold text-2xl tabular-nums'
				}
			>
				{props.value.toLocaleString('it-IT')}
			</span>
			<span className='text-muted-foreground text-xs'>{props.label}</span>
		</div>
	)
}

function refreshPhase(inFlight: boolean, done: boolean) {
	if (inFlight) {
		return 'loading'
	}
	return done ? 'success' : 'idle'
}

function refreshLabel(done: boolean, inFlight: boolean): TKey {
	if (done) {
		return 'po:dashboard.refresh_done'
	}
	// Waiting for the agent or already syncing, one label: to the expert it is
	// the same moment, the data is on its way.
	return inFlight
		? 'po:dashboard.refresh_syncing'
		: 'po:dashboard.refresh_now'
}
